import * as signalR from "@microsoft/signalr";

/**
 * Manages multiple SignalR hub connections with:
 * - Automatic reconnection (exponential backoff: 0s, 2s, 10s, 30s, 60s)
 * - Token-based authentication (JWT via bearer token)
 * - Lifecycle event handlers (reconnecting, reconnected, closed)
 * - Handler tracking for proper cleanup
 * - Reverse proxy support via apiUrlCombine
 */

/**
 * Handler reference for proper unsubscribe.
 * Stores the original callback so we can remove it specifically.
 */
interface HandlerReference {
  eventName: string;
  handler: (...args: any[]) => void;
}
export class SignalRClient {
  //
  // Private Properties
  //
  private getToken: () => string | null;
  private onStateChange?: () => void;
  private connections = new Map<string, signalR.HubConnection>();

  // Maps hub name to event handlers for proper cleanup.
  private handlers = new Map<string, Map<string, HandlerReference>>();

  //
  // Constructor
  //
  constructor(getToken: () => string | null) {
    this.getToken = getToken;
  }

  //
  // State Change Management
  //

  /**
   * Register callback for connection state changes.
   * Called when any hub connects, disconnects, or reconnects.
   *
   * @param cb - Callback function to invoke on state change
   */
  setOnStateChange(cb: () => void) {
    this.onStateChange = cb;
  }

  //
  // Connection Management
  //

  /**
   * Create or retrieve a SignalR hub connection.
   *
   * Handles existing connections intelligently:
   * - If connected: return existing connection
   * - If connecting: wait for connection to complete
   * - If disconnected: create new connection
   *
   * @param hubName - Unique identifier for the hub (e.g., "logs", "master")
   * @param endpoint - Hub endpoint path (e.g., "/hubs/logs")
   * @param forceNew - Force creation of a new connection (disconnects existing)
   * @returns Active SignalR hub connection
   * @throws Error if connection fails after retries
   */
  async createConnection(hubName: string, endpoint: string, forceNew: boolean = false): Promise<signalR.HubConnection> {
    // Handle existing connection
    if (this.connections.has(hubName)) {
      const existing = this.connections.get(hubName)!;

      if (forceNew) {
        await this.safeDisconnect(existing);
        this.connections.delete(hubName);
        this.handlers.delete(hubName);
      } else if (existing.state === signalR.HubConnectionState.Connected) {
        return existing;
      } else if (existing.state === signalR.HubConnectionState.Connecting) {
        return this.waitForConnection(existing);
      } else {
        // Disconnected state - remove and create new
        this.connections.delete(hubName);
        this.handlers.delete(hubName);
      }
    }

    // Create new connection
    const connection = this.buildConnection(hubName, endpoint);

    // Set up lifecycle event handlers
    this.setupLifecycleHandlers(connection, hubName);

    // Start connection with error handling
    try {
      await connection.start();
      this.connections.set(hubName, connection);
      this.onStateChange?.();

      return connection;
    } catch (error) {
      console.error(`❌ Failed to connect to ${hubName}:`, error);
      throw new Error(
        `SignalR connection failed for ${hubName}: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * Get an existing hub connection.
   *
   * @param hubName - Hub identifier
   * @returns Hub connection or null if not found
   */
  async getConnection(hubName: string): Promise<signalR.HubConnection | null> {
    return this.connections.get(hubName) || null;
  }

  /**
   * Check if a specific hub is connected.
   *
   * @param hubName - Hub identifier
   * @returns True if hub is connected
   */
  isHubConnected(hubName: string): boolean {
    const connection = this.connections.get(hubName);
    return connection?.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Check if any hub is connected.
   *
   * @returns True if at least one hub is connected
   */
  isConnected(): boolean {
    return Array.from(this.connections.values()).some((conn) => conn.state === signalR.HubConnectionState.Connected);
  }

  //
  // Event Subscription Management
  //

  /**
   * Subscribe to a SignalR event with handler tracking.
   *
   * Returns a unique handler ID that can be used for specific unsubscribe.
   *
   * @param hubName - Hub identifier
   * @param eventName - Event name to subscribe to
   * @param handler - Event handler function
   * @returns Unique handler ID for unsubscribe
   */
  on(hubName: string, eventName: string, handler: (...args: any[]) => void): string {
    const connection = this.connections.get(hubName);
    if (!connection) {
      throw new Error(`No connection found for hub: ${hubName}`);
    }

    // Generate unique handler ID
    const handlerId = `${eventName}_${Date.now()}_${Math.random()}`;

    // Store handler reference
    if (!this.handlers.has(hubName)) {
      this.handlers.set(hubName, new Map());
    }
    this.handlers.get(hubName)!.set(handlerId, { eventName, handler });

    // Subscribe to event
    connection.on(eventName, handler);

    return handlerId;
  }

  /**
   * Unsubscribe from a SignalR event using handler ID.
   *
   * Only removes the specific handler, not all handlers for the event.
   *
   * @param hubName - Hub identifier
   * @param eventName - Event name (optional, for validation)
   * @param handlerId - Unique handler ID returned from `on()`
   * @returns True if handler was found and removed
   */
  off(hubName: string, eventName: string, handlerId: string): boolean {
    const connection = this.connections.get(hubName);
    if (!connection) {
      console.warn(`⚠️  No connection found for hub: ${hubName}`);
      return false;
    }

    const hubHandlers = this.handlers.get(hubName);
    if (!hubHandlers) {
      console.warn(`⚠️  No handlers found for hub: ${hubName}`);
      return false;
    }

    const handlerRef = hubHandlers.get(handlerId);
    if (!handlerRef) {
      console.warn(`⚠️  Handler ${handlerId} not found for ${hubName}.${eventName}`);
      return false;
    }

    // Validate event name matches
    if (handlerRef.eventName !== eventName) {
      console.warn(`⚠️  Event name mismatch: expected ${handlerRef.eventName}, got ${eventName}`);
      return false;
    }

    // Remove specific handler from connection
    connection.off(eventName, handlerRef.handler);

    // Remove handler reference
    hubHandlers.delete(handlerId);

    return true;
  }

  //
  // Disconnection Management
  //

  /**
   * Disconnect all active hub connections.
   *
   * Cleans up:
   * - All connections
   * - All handler references
   * - State change callback invocation
   *
   */
  async disconnectAll(): Promise<void> {
    if (this.connections.size === 0) {
      return;
    }

    const disconnectPromises = Array.from(this.connections.entries()).map(async ([hubName, conn]) => {
      try {
        await this.safeDisconnect(conn);
      } catch (error) {
        console.warn(`⚠️  Failed to disconnect ${hubName}:`, error);
      }
    });

    await Promise.all(disconnectPromises);
    this.connections.clear();
    this.handlers.clear();
    this.onStateChange?.();
  }

  /**
   * Disconnect a specific hub.
   *
   * @param hubName - Hub identifier
   */
  async disconnect(hubName: string): Promise<void> {
    const connection = this.connections.get(hubName);

    if (!connection) {
      console.warn(`⚠️  No connection found for ${hubName}`);
      return;
    }

    try {
      await this.safeDisconnect(connection);
      this.connections.delete(hubName);
      this.handlers.delete(hubName);
    } catch (error) {
      console.error(`❌ Failed to disconnect ${hubName}:`, error);
      throw error;
    }
  }

  //
  // Private Helper Methods
  //

  /**
   * Build a new SignalR connection with proper configuration.
   *
   * Configuration:
   * - JWT bearer token authentication
   * - Automatic reconnect with exponential backoff
   * - Warning-level logging
   * - Reverse proxy support via apiUrlCombine
   *
   * Responsibilities:
   * - Construct hub URL with base URL prefix for reverse proxy
   * - Configure authentication token factory
   * - Set up automatic reconnection strategy
   * - Configure logging level
   *
   * @param hubName - Hub identifier for logging
   * @param endpoint - Hub endpoint path (e.g., "/hubs/logs", "/hubs/master")
   * @returns Configured SignalR HubConnection
   */
  private buildConnection(hubName: string, endpoint: string): signalR.HubConnection {
    // Get API base URL from runtime config (client-side only)
    const config = useRuntimeConfig();
    const apiBaseUrl = config.public.API.baseUrl?.replace(/\/api$/, "") || "";
    const hubUrl = apiUrlCombine(apiBaseUrl, endpoint);

    console.log(`[SignalR] Building connection to ${hubUrl}`);

    return new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => {
          const token = this.getToken();
          if (!token) {
            console.warn(`⚠️  No auth token available for ${hubName}`);
          }
          return token || "";
        },
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();
  }

  /**
   * Set up connection lifecycle event handlers.
   *
   * Handlers:
   * - onreconnecting: Invoked when connection starts reconnecting
   * - onreconnected: Invoked when reconnection succeeds
   * - onclose: Invoked when connection closes (clean or error)
   */
  private setupLifecycleHandlers(connection: signalR.HubConnection, hubName: string): void {
    connection.onreconnecting((error) => {
      this.onStateChange?.();
      console.warn(`🔄 ${hubName} reconnecting...`, error?.message);
    });

    connection.onreconnected((connectionId) => {
      this.onStateChange?.();
      console.log(`✅ ${hubName} reconnected (ID: ${connectionId})`);
    });

    connection.onclose((error) => {
      if (error) {
        console.error(`🔌 ${hubName} connection closed with error:`, error);
      } else {
        console.log(`🔌 ${hubName} connection closed`);
      }

      this.onStateChange?.();
      this.connections.delete(hubName);
      this.handlers.delete(hubName);
    });
  }

  /**
   * Wait for a connecting connection to complete.
   *
   * Polls connection state every 100ms with 30s timeout.
   *
   * @param connection - Connection in connecting state
   * @returns Connected connection
   */
  private waitForConnection(connection: signalR.HubConnection): Promise<signalR.HubConnection> {
    return new Promise((resolve, reject) => {
      const config = useRuntimeConfig();
      const connectionTimeout = config.public.signalr.connectionTimeout || 30000;
      const timeout = setTimeout(() => {
        reject(new Error(`Connection timeout after ${connectionTimeout / 1000}s`));
      }, connectionTimeout);

      const checkConnection = () => {
        if (connection.state === signalR.HubConnectionState.Connected) {
          clearTimeout(timeout);
          resolve(connection);
        } else if (connection.state === signalR.HubConnectionState.Disconnected) {
          clearTimeout(timeout);
          reject(new Error("Connection failed"));
        } else {
          setTimeout(checkConnection, 100);
        }
      };

      checkConnection();
    });
  }

  /**
   * Safely disconnect a connection (ignores if already disconnected).
   *
   * @param connection - Connection to disconnect
   */
  private async safeDisconnect(connection: signalR.HubConnection): Promise<void> {
    if (connection.state !== signalR.HubConnectionState.Disconnected) {
      await connection.stop();
    }
  }
}
